import { Button, FormControlLabel, Switch } from '@mui/material';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { required } from 'components/helpers/validators';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option, OptionDescription } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { equipmentService, productService } from 'shared/services';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { productWarehouseHistoryService } from 'features/inventory/warehouses/api/product-warehouse-history.service';
import {
  EnhancedForm,
  IdentityField,
  AutocompleteAdapter,
  DateTimePickerAdapter
} from 'components/enhanced';
import { WarehouseProductsHistoryLineColumns } from 'features/settings/history/product-warehouse-history-line';

const ACTION_OPTIONS: Pick<Option, 'id' | 'code'>[] = [
  { id: 1, code: 'COLLEGA' },
  { id: 2, code: 'SCOLLEGA' }
];

interface ITabProductWarehouseHistoryLinkParams {
  title: string;
  layer: string;
  entity_id: number;
}

type LinkEntityType = 'product' | 'equipment';

function ProductWarehouseHistoryLinkTab(params: ITabProductWarehouseHistoryLinkParams) {
  const { t } = useTranslation();
  const { title, layer, entity_id } = params;
  const formId = `add-tab-layer-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const isWorkstationLayer = layer === 'workstation';

  const [entityType, setEntityType] = useState<LinkEntityType>('product');
  const [entities, setEntities] = useState<OptionDescription[]>([]);
  const [warehouses, setWarehouses] = useState<OptionDescription[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);

  const historyRefresh = useRef(null);
  const effectiveEntityType: LinkEntityType = isWorkstationLayer ? entityType : 'product';
  const showWarehouseField = effectiveEntityType === 'product';

  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  useEffect(() => {
    if (!showWarehouseField) {
      setSelectedWarehouseId(null);
      equipmentService.getAllBaseInformation().then(setEntities);
      return;
    }

    if (!selectedWarehouseId) {
      setEntities([]);
      return;
    }
    productService.getAllBaseInformation({ warehouse_id: selectedWarehouseId }).then(setEntities);
  }, [selectedWarehouseId, showWarehouseField]);

  const actionsOptions = useMemo<OptionDescription[]>(
    () =>
      ACTION_OPTIONS.map((item) => ({
        ...item,
        description: item.code
      })),
    [t]
  );

  // -------------------------------------------------------------- OPERATIONS
  const submitNew = (values) => {
    const selectedEntity = values?.entity;
    const entityId = Number(selectedEntity?.id ?? selectedEntity);
    const entityCode = String(selectedEntity?.code ?? selectedEntity?.description ?? '');

    const payload = {
      object_id: entity_id,
      content_type_name: layer,
      action: values?.action,
      timestamp: values?.timestamp,
      warehouse: showWarehouseField ? values?.warehouse : undefined,
      entity_type: effectiveEntityType,
      entity_id: entityId,
      entity_code: entityCode
    };

    productWarehouseHistoryService.add(payload).then((res) => {
      if (res) {
        toast.success(t('actions.successfully.added'));
        historyRefresh.current();
      }
    });
  };

  const columnsHisotryLink = WarehouseProductsHistoryLineColumns(historyRefresh);

  return (
    <>
      <EnhancedAccordion title={title} subtitle={t('actions.add-new-history-line')}>
        <EnhancedForm
          onSubmit={submitNew}
          formId={formId}
          autoStyleLayout={true}
          columns={2}
          costumControls={
            <Button type="submit" form={formId} variant="outlined" fullWidth>
              {t('global.add')}
            </Button>
          }>
          {isWorkstationLayer && (
            <div className="mb-2 w-full">
              <FormControlLabel
                control={
                  <Switch
                    checked={entityType === 'equipment'}
                    onChange={(event) => {
                      setEntityType(event.target.checked ? 'equipment' : 'product');
                    }}
                  />
                }
                label={entityType === 'equipment' ? t('global.equipment') : t('global.product')}
              />
            </div>
          )}
          {showWarehouseField && (
            <IdentityField
              name="warehouse"
              component={AutocompleteAdapter}
              options={warehouses}
              label={t('global.warehouse')}
              type="text"
              validate={required}
              setValue={(value) => setSelectedWarehouseId(value ? Number(value.id) : null)}
              required
            />
          )}
          <IdentityField
            name="entity"
            component={AutocompleteAdapter}
            options={entities}
            label={
              effectiveEntityType === 'equipment' ? t('global.equipment') : t('global.product')
            }
            type="text"
            disabled={showWarehouseField && !selectedWarehouseId}
            optionLabel={'description'}
            submitObject
            validate={required}
            required
          />
          <IdentityField
            name="timestamp"
            component={DateTimePickerAdapter}
            label={t('global.datetime')}
          />
          <IdentityField
            name="action"
            component={AutocompleteAdapter}
            options={actionsOptions}
            label={t('global.action')}
            required
          />
        </EnhancedForm>
      </EnhancedAccordion>
      <EnhancedCard>
        <EnhancedDataGrid
          columns={columnsHisotryLink}
          service={productWarehouseHistoryService}
          outerParams={{ content_type_name: layer, content_object_id: entity_id }}
          refresh={historyRefresh}
        />
      </EnhancedCard>
    </>
  );
}

export default ProductWarehouseHistoryLinkTab;
